import { toGenericPriceKey, toOracleKey } from '../../assets'
import { isUnearnableRate } from '../rateSanity'
import { GenericTokenList, MulticallRetryFunction } from '../../types'
import { parseRawAmount } from '../../utils/parsing'
import { composeVaultDisplayName } from '../displayName'
import { fetchYearnApiVaults, hasYearnVaults, type YDaemonVault } from './api'
import { YearnVault, YearnVaults } from './types'

const BRAND = 'Yearn'
const ZERO_ADDR = '0x0000000000000000000000000000000000000000'

/**
 * Minimal Yearn V3 surface used for the on-chain liquidity read. Both
 * VaultV3 (multi-strategy) and TokenizedStrategy (single-strategy) are
 * covered — calls that don't exist on a given kind simply fail under
 * `allowFailure` and are ignored.
 */
const YearnLiquidityAbi = [
  {
    type: 'function',
    name: 'availableWithdrawLimit',
    stateMutability: 'view',
    inputs: [{ name: 'owner', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'totalIdle',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'get_default_queue',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address[]' }],
  },
  {
    type: 'function',
    name: 'maxWithdraw',
    stateMutability: 'view',
    inputs: [{ name: 'owner', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

const asBig = (v: unknown): bigint | null => {
  try {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number' && Number.isFinite(v))
      return BigInt(Math.trunc(v))
    if (typeof v === 'string' && /^\d+$/.test(v)) return BigInt(v)
    return null
  } catch {
    return null
  }
}

const num = (v: number | null | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

/** APR fraction → percent (`0.0512` → `5.12`), guarding NaN/Infinity. */
const toPct = (v: number | null | undefined): number => {
  const n = num(v)
  return n == null ? 0 : n * 100
}

/**
 * Derive total shares from `totalAssets` and `pricePerShare` — yDaemon
 * doesn't report supply directly. For an ERC-4626,
 * `pricePerShare = convertToAssets(10**dec) = totalAssets * 10**dec / totalSupply`,
 * so `totalSupply = totalAssets * 10**dec / pricePerShare`. Falls back to
 * `totalAssets` (1:1) when the price-per-share is missing/zero (brand-new
 * vault), and to `'0'` when there are no assets.
 */
const deriveTotalSupply = (
  totalAssetsRaw: string,
  pricePerShareRaw: string,
  decimals: number,
): string => {
  try {
    const assets = BigInt(totalAssetsRaw || '0')
    if (assets === 0n) return '0'
    const pps = BigInt(pricePerShareRaw || '0')
    if (pps <= 0n) return totalAssetsRaw
    const scale = 10n ** BigInt(decimals)
    return ((assets * scale) / pps).toString()
  } catch {
    return totalAssetsRaw || '0'
  }
}

function parseVault(
  v: YDaemonVault,
  chainId: string,
  prices: { [asset: string]: number },
  tokenList: GenericTokenList,
): YearnVault | null {
  const address = (v?.address ?? '').toLowerCase()
  const assetAddr = (v?.token?.address ?? '').toLowerCase()
  if (!address || !assetAddr) return null

  const assetMeta = tokenList[assetAddr]
  const decimals = Number(v.decimals ?? 18)
  const assetDecimals = Number(v.token?.decimals ?? assetMeta?.decimals ?? 18)

  const totalAssetsRaw = v.tvl?.totalAssets?.toString() ?? '0'
  const pricePerShare = v.pricePerShare?.toString() ?? '0'
  const totalSupplyRaw = deriveTotalSupply(
    totalAssetsRaw,
    pricePerShare,
    decimals,
  )
  const totalAssetsFormatted = Number(
    parseRawAmount(totalAssetsRaw, assetDecimals),
  )

  // Realized net APR (already net of the performance fee). Fall back to
  // the forward (expected) APR for vaults too new to have a TWRR.
  const realized = num(v.apr?.netAPR)
  const forward = num(v.apr?.forwardAPR?.netAPR)
  const isForwardApr = realized == null && forward != null
  const supplyRate = toPct(realized ?? forward)
  const rewardsRate = toPct(v.apr?.extra?.stakingRewardsAPR)
  const fee = toPct(v.apr?.fees?.performance)

  // Underlying USD: prefer our price map (oracle-keyed, consistent with
  // the rest of the pipeline); fall back to yDaemon's own `tvl.price`.
  const oracleKey =
    toOracleKey(assetMeta?.assetGroup) ?? toGenericPriceKey(assetAddr, chainId)
  const apiPrice = num(v.tvl?.price) ?? 0
  const priceUsd = prices[oracleKey] ?? prices[assetAddr] ?? apiPrice

  const apiTvlUsd = num(v.tvl?.tvl) ?? 0
  const totalAssetsUsd = apiTvlUsd || totalAssetsFormatted * priceUsd

  const name = (v.name ?? '').trim()
  const symbol = (v.symbol ?? '').trim()
  const displayName = composeVaultDisplayName(BRAND, BRAND, assetMeta, name)

  return {
    address,
    underlying: assetAddr,
    symbol,
    name: name || symbol,
    displayName,
    curatorName: BRAND,
    decimals,
    assetDecimals,
    totalAssets: totalAssetsRaw,
    totalSupply: totalSupplyRaw,
    pricePerShare,
    supplyRate,
    rewardsRate,
    depositRate: supplyRate + rewardsRate,
    fee,
    isForwardApr,
    kind: v.kind ?? 'Multi Strategy',
    version: v.version ?? '',
    ...(v.category ? { category: v.category } : {}),
    asset: assetMeta,
    priceUsd,
    totalAssetsFormatted,
    totalAssetsUsd,
  }
}

/** Set the liquidity fields on a parsed vault from a raw withdrawable
 *  amount (clamping is done by the caller). */
const setLiquidity = (v: YearnVault, liq: bigint): void => {
  v.liquidity = liq.toString()
  v.liquidityFormatted = Number(parseRawAmount(v.liquidity, v.assetDecimals))
  v.liquidityUsd =
    v.priceUsd != null
      ? v.liquidityFormatted * v.priceUsd
      : v.liquidityFormatted
}

/**
 * Attach on-chain withdrawable liquidity to each parsed vault, in place.
 * Best-effort: any failure leaves `liquidity` undefined (the snapshot is
 * still returned). Two reader shapes:
 *   - **TokenizedStrategy** (single-strategy) exposes
 *     `availableWithdrawLimit(owner)` — a vault-level cap (often max-uint
 *     ⇒ fully liquid). `liquidity = min(totalAssets, cap)`.
 *   - **VaultV3** (multi-strategy) has no such getter; the withdrawable
 *     amount is `totalIdle + Σ strategy.maxWithdraw(vault)` over the
 *     default queue. Needs a second multicall round for the per-strategy
 *     reads. `liquidity = min(totalAssets, idle + Σ)`.
 */
const attachYearnLiquidity = async (
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  out: YearnVaults,
): Promise<void> => {
  const vaults = Object.values(out)
  if (vaults.length === 0) return

  // Round 1 — per vault: availableWithdrawLimit (single), get_default_queue
  // + totalIdle (multi). allowFailure so wrong-shape calls just drop out.
  const r1Calls = vaults.flatMap((v) => [
    { address: v.address, name: 'availableWithdrawLimit', params: [ZERO_ADDR] },
    { address: v.address, name: 'get_default_queue', params: [] },
    { address: v.address, name: 'totalIdle', params: [] },
  ])
  const r1 = (await multicallRetry({
    chain: chainId,
    calls: r1Calls as any,
    abi: r1Calls.map(() => YearnLiquidityAbi) as any,
    maxRetries: 2,
    allowFailure: true,
  })) as unknown[]

  // Multi-strategy vaults whose liquidity needs the per-strategy round.
  const pending: {
    v: YearnVault
    idle: bigint
    queue: string[]
    totalAssets: bigint
  }[] = []

  for (let i = 0; i < vaults.length; i++) {
    const v = vaults[i]
    const totalAssets = asBig(v.totalAssets) ?? 0n
    const awl = asBig(r1[i * 3])
    const queueRaw = r1[i * 3 + 1]
    const idle = asBig(r1[i * 3 + 2]) ?? 0n

    if (awl != null) {
      setLiquidity(v, awl >= totalAssets ? totalAssets : awl)
    } else if (Array.isArray(queueRaw) && queueRaw.length > 0) {
      pending.push({
        v,
        idle,
        queue: queueRaw.map((a) => String(a).toLowerCase()),
        totalAssets,
      })
    } else if (asBig(r1[i * 3 + 2]) != null) {
      // Multi-strategy with an empty queue — only idle is liquid (rare).
      setLiquidity(v, idle >= totalAssets ? totalAssets : idle)
    }
    // else: no usable read → leave liquidity undefined.
  }

  if (pending.length === 0) return

  // Round 2 — per (vault, strategy): strategy.maxWithdraw(vault) = the
  // amount the vault can pull from that strategy right now.
  const r2Calls = pending.flatMap((p) =>
    p.queue.map((s) => ({
      address: s,
      name: 'maxWithdraw',
      params: [p.v.address],
    })),
  )
  const r2 = (await multicallRetry({
    chain: chainId,
    calls: r2Calls as any,
    abi: r2Calls.map(() => YearnLiquidityAbi) as any,
    maxRetries: 2,
    allowFailure: true,
  })) as unknown[]

  let idx = 0
  for (const p of pending) {
    let sum = p.idle
    for (let j = 0; j < p.queue.length; j++) {
      const w = asBig(r2[idx++])
      if (w != null) sum += w
    }
    setLiquidity(p.v, sum >= p.totalAssets ? p.totalAssets : sum)
  }
}

/**
 * One-shot fetcher for all endorsed Yearn V3 vaults on a chain.
 *
 * Discovery is HTTP (yDaemon REST), then a best-effort on-chain multicall
 * adds withdrawable `liquidity` per vault (see {@link attachYearnLiquidity}).
 * Returns an empty map for chains without a Yearn V3 deployment.
 *
 * @param chainId         target chain
 * @param multicallRetry  provider-level multicall executor (used only for
 *                        the liquidity leg; discovery is HTTP)
 * @param prices          optional price map keyed by oracle key / underlying
 * @param tokenList       optional token list for hydrating `asset` metadata
 *
 * @returns `YearnVaults` — map keyed by lowercased vault address.
 */
export const fetchYearnVaults = async (
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
): Promise<YearnVaults> => {
  if (!hasYearnVaults(chainId)) return {}

  const items = await fetchYearnApiVaults(chainId)
  const out: YearnVaults = {}
  for (const item of items) {
    const parsed = parseVault(item, chainId, prices, tokenList)
    if (!parsed) continue
    // Yearn's own `netAPR` can be a degenerate window measurement on a vault
    // holding dust — ysOETH published 5,755,485 % off $341 of assets, a
    // month-ago point on 0.138 OETH. Both halves are required, so a real
    // vault at a wild rate is still reported (and investigated) rather than
    // quietly deleted. See `isUnearnableRate`.
    if (
      isUnearnableRate({
        aprPercent: parsed.supplyRate,
        totalAssetsUsd: parsed.totalAssetsUsd,
      })
    ) {
      continue
    }
    out[parsed.address] = parsed
  }

  // Liquidity is a best-effort enrichment — a multicall/RPC failure must
  // not drop the snapshot (mirrors Fluid's exposures pattern).
  try {
    await attachYearnLiquidity(chainId, multicallRetry, out)
  } catch {
    // leave liquidity undefined on every entry
  }

  return out
}
